
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Accordion } from "@/components/ui/accordion";
import { Clock, Check, X } from "lucide-react";
import { RemoveGuestDialog } from "@/components/event-details/add-guests/components/RemoveGuestDialog";
import { GuestFeedbackDialog } from "@/components/event-details/guest-list/GuestFeedbackDialog";
import { useEventGuests } from "@/hooks/use-event-guests";
import { GuestGroup } from "./guest-list/GuestGroup";
import { useProcessInvites, GuestInfo } from "@/hooks/use-process-invites";
import { InlineGuestForm } from "./add-guests/InlineGuestForm";

interface InvitedGuestListProps {
  eventId: string;
  isHost: boolean;
  onRemoveGuest?: (inviteId: string) => Promise<void>;
  status?: string;
}

export const InvitedGuestList = ({
  eventId,
  isHost,
  onRemoveGuest,
  status = 'planned'
}: InvitedGuestListProps) => {
  const [guestToRemove, setGuestToRemove] = useState<GuestInfo | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<GuestInfo | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  
  const { toast } = useToast();
  
  const {
    invites,
    isLoadingInvites: isLoading,
    refetch
  } = useEventGuests(eventId);
  
  const {
    pendingGuests,
    attendingGuests,
    notAttendingGuests,
    totalGuests
  } = useProcessInvites(invites);
  
  const handleRemoveGuest = (guest: GuestInfo) => {
    setGuestToRemove(guest);
  };
  
  const confirmRemoveGuest = async () => {
    if (!guestToRemove || !onRemoveGuest) {
      setGuestToRemove(null);
      return;
    }
    
    setIsRemoving(true);
    
    try {
      // Use the invite_id or user_id depending on what's available
      const idToRemove = guestToRemove.invite_id;
      
      // Wait for the removal to complete
      await onRemoveGuest(idToRemove);
      
      // Success is handled by the mutation in useAttendeeRemoval
      // and will automatically refresh the data
    } catch (error) {
      console.error('Error removing guest:', error);
      toast({
        title: "Error",
        description: "Failed to remove guest. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(false);
      setGuestToRemove(null);
    }
  };
  
  const handleViewFeedback = (guest: GuestInfo) => {
    setSelectedGuest(guest);
  };
  
  if (isLoading) {
    return <div className="text-center py-4">Loading guests...</div>;
  }

  // For confirmed or booked events, use the same UI as planned events but only show attending guests
  if (status === 'confirmed' || status === 'booked') {
    return (
      <div className="space-y-4">
        {/* Removed the header */}
        
        {attendingGuests.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No confirmed guests yet.</div>
        ) : (
          <Accordion type="multiple" defaultValue={['attending']} className="space-y-2">
            <GuestGroup 
              title="Attending" 
              icon={<Check className="h-4 w-4 text-green-500" />} 
              guests={attendingGuests} 
              status="accepted" 
              isHost={isHost} 
              onViewFeedback={handleViewFeedback} 
              onRemoveGuest={isHost ? handleRemoveGuest : undefined} 
            />
          </Accordion>
        )}

        <GuestFeedbackDialog 
          isOpen={!!selectedGuest} 
          onClose={() => setSelectedGuest(null)} 
          eventId={eventId} 
          guestName={selectedGuest?.display_name || ''} 
          phoneNumber={selectedGuest?.phone_number} 
          rsvpStatus={selectedGuest?.rsvp_status} 
          suggestedVenue={selectedGuest?.suggested_venue} 
          suggestedTime={selectedGuest?.suggested_time}
          selectedDates={selectedGuest?.selected_dates}
          selectedTimes={selectedGuest?.selected_times} 
          notes={selectedGuest?.feedback_text} 
        />
        
        <RemoveGuestDialog 
          isOpen={!!guestToRemove} 
          onClose={() => setGuestToRemove(null)} 
          onConfirm={confirmRemoveGuest} 
          guestName={guestToRemove?.display_name || 'Guest'}
          isLoading={isRemoving} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isHost && (
        <div className="mb-6">
          <InlineGuestForm 
            eventId={eventId} 
            onSuccess={() => {
              refetch();
            }} 
          />
        </div>
      )}
      
      {totalGuests === 0 ? (
        <div className="text-center py-4 text-gray-500">No guests invited yet.</div>
      ) : (
        <Accordion type="multiple" defaultValue={['attending', 'invited', 'not-attending']} className="space-y-2">
          <GuestGroup 
            title="Attending" 
            icon={<Check className="h-4 w-4 text-green-500" />} 
            guests={attendingGuests} 
            status="accepted" 
            isHost={isHost} 
            onViewFeedback={handleViewFeedback} 
            onRemoveGuest={isHost ? handleRemoveGuest : undefined} 
          />
          
          <GuestGroup 
            title="Invited" 
            icon={<Clock className="h-4 w-4 text-gray-500" />} 
            guests={pendingGuests} 
            status="invited" 
            isHost={isHost} 
            onViewFeedback={handleViewFeedback} 
            onRemoveGuest={isHost ? handleRemoveGuest : undefined} 
          />
          
          <GuestGroup 
            title="Not Attending" 
            icon={<X className="h-4 w-4 text-red-500" />} 
            guests={notAttendingGuests} 
            status="declined" 
            isHost={isHost} 
            onViewFeedback={handleViewFeedback} 
            onRemoveGuest={isHost ? handleRemoveGuest : undefined} 
          />
        </Accordion>
      )}

      <RemoveGuestDialog 
        isOpen={!!guestToRemove} 
        onClose={() => setGuestToRemove(null)} 
        onConfirm={confirmRemoveGuest} 
        guestName={guestToRemove?.display_name || 'Guest'} 
        isLoading={isRemoving}
      />

      <GuestFeedbackDialog 
        isOpen={!!selectedGuest} 
        onClose={() => setSelectedGuest(null)} 
        eventId={eventId} 
        guestName={selectedGuest?.display_name || ''} 
        phoneNumber={selectedGuest?.phone_number} 
        rsvpStatus={selectedGuest?.rsvp_status} 
        suggestedVenue={selectedGuest?.suggested_venue} 
        suggestedTime={selectedGuest?.suggested_time}
        selectedDates={selectedGuest?.selected_dates}
        selectedTimes={selectedGuest?.selected_times} 
        notes={selectedGuest?.feedback_text} 
      />
    </div>
  );
};
